import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Modal from './Modal';
import InlineLoader from '../Loader/InlineLoader';
import PatientImage from '../Patient/PatientImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faArrowLeft, faChevronRight, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthProvider';
import { API_BASE_URL } from '../../api';

// "Changer de compte" (sidebar user menu): pick another user of the same pharmacy, enter
// *their* password (and 2FA code if they have it on) — the backend swaps the session cookie
// (POST /api/auth/switch). On success the page is fully reloaded so nothing from the previous
// user's session (cart, cached lists, per-user bookmarks) lingers in memory.
export default function SwitchAccountModal({ show, onClose }) {
  const { settings } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [password, setPassword] = useState('');
  const [pendingToken, setPendingToken] = useState(null);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setSelected(null);
    setPassword('');
    setPendingToken(null);
    setCode('');
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/auth/switch-accounts`, { withCredentials: true })
      .then(({ data }) => setAccounts(data || []))
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false));
  }, [show]);

  // focus the password / code field as soon as its step shows up
  useEffect(() => {
    if (selected) inputRef.current?.focus();
  }, [selected, pendingToken]);

  const finish = () => {
    const defaultPage = settings?.general_general_settings_default_page || '/dashboard';
    window.location.assign(defaultPage);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/switch`, { userId: selected.id, password }, { withCredentials: true });
      finish();
    } catch (err) {
      if (err.response?.data?.code === '2FA_REQUIRED') {
        setPendingToken(err.response.data.token);
      } else {
        toast.error(err.response?.data?.message || 'Impossible de changer de compte.');
      }
      setSubmitting(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!code || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/login/2fa`, { pendingToken, code }, { withCredentials: true });
      finish();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Code invalide.');
      setSubmitting(false);
    }
  };

  const back = () => {
    setSelected(null);
    setPassword('');
    setPendingToken(null);
    setCode('');
  };

  return (
    <Modal
      title="Changer de compte"
      show={show}
      onClose={onClose}
      icon={faUsers}
      iconBg="#eff6ff"
      iconColor="#1d4ed8"
    >
      {!selected ? (
        <>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
            Connectez-vous avec un autre utilisateur de la pharmacie, sans vous déconnecter d'abord.
          </p>

          {loading ? (
            <InlineLoader small />
          ) : accounts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 13 }}>
              Aucun autre utilisateur actif dans cette pharmacie.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {accounts.map((account) => (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => setSelected(account)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'start',
                    padding: '10px 12px', border: '1px solid #f0f1f3', borderRadius: 10, background: '#fff',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                >
                  <PatientImage patient={account} size={38} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>
                      {account.firstName} {account.lastName}
                    </div>
                    {account.roles?.length > 0 && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{account.roles.join(', ')}</div>
                    )}
                  </div>
                  <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11, color: '#9ca3af' }} />
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <form onSubmit={pendingToken ? submitCode : submitPassword}>
          <button type="button" className="btn btn-link btn-sm p-0 mb-3" onClick={back} style={{ fontSize: 13, textDecoration: 'none' }}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Choisir un autre compte
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <PatientImage patient={selected} size={44} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{selected.firstName} {selected.lastName}</div>
              {selected.roles?.length > 0 && <div style={{ fontSize: 12, color: '#6b7280' }}>{selected.roles.join(', ')}</div>}
            </div>
          </div>

          {pendingToken ? (
            <>
              <label htmlFor="switch-account-code" className="form-label" style={{ fontSize: 13 }}>
                <FontAwesomeIcon icon={faShieldHalved} className="me-1" /> Code de vérification (application d'authentification)
              </label>
              <input
                ref={inputRef}
                id="switch-account-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="form-control"
                value={code}
                onChange={e => setCode(e.target.value.trim())}
              />
            </>
          ) : (
            <>
              <label htmlFor="switch-account-password" className="form-label" style={{ fontSize: 13 }}>
                Mot de passe de {selected.firstName}
              </label>
              <input
                ref={inputRef}
                id="switch-account-password"
                type="password"
                autoComplete="off"
                className="form-control"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </>
          )}

          <button
            type="submit"
            className="btn btn-primary w-100 mt-3"
            disabled={submitting || (pendingToken ? !code : !password)}
          >
            {submitting ? <span className="spinner-border spinner-border-sm" /> : 'Se connecter'}
          </button>
        </form>
      )}
    </Modal>
  );
}
